import { getQueryStringFromOptions, getResponse } from '@satellite/services/serviceUtilities';
import { cacheableGet } from '@satellite/services/httpClient';

export default {
  async createYardTask(params, options = {}, requestConfig = {}) {
    const response = await axios.post('/yard-task', params, requestConfig);

    return getResponse(response, options);
  },
  /**
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   *
   * Get yard tasks grid with improved API contract
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetQueryStringOptions} [options.query] - Query string options (fields, joins)
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<TResponse>} The response data
   */
  async getYardTasksGrid({
    query = {},
    getResponseConfig = {},
    requestConfig = {},
    enableCache = false
  } = {}) {
    const queryString = getQueryStringFromOptions(query || {});
    const url = `/yard-task/grid${queryString ? `?${queryString}` : ''}`;

    const axiosResponse = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },

  /**
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   *
   * Get yard task list with improved API contract
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetQueryStringOptions} [options.query] - Query string options (fields, joins)
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<TResponse>} The response data
   */
  async getYardTaskList({
    query = {},
    getResponseConfig = {},
    requestConfig = {},
    enableCache = false
  } = {}) {
    const queryString = getQueryStringFromOptions(query || {});
    const url = `/yard-task${queryString ? `?${queryString}` : ''}`;

    const axiosResponse = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },

  /**
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   *
   * Get yard task by ID with improved API contract
   * @param {string} taskId - Task ID
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetQueryStringOptions} [options.query] - Query string options (fields, joins)
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<TResponse>} The response data
   */
  async getYardTask(
    taskId,
    { getResponseConfig = {}, requestConfig = {}, enableCache = false } = {}
  ) {
    const url = `/yard-task/${taskId}`;

    const axiosResponse = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },

  async getYardTaskTimeline(
    taskId,
    { getResponseConfig = {}, requestConfig = {}, enableCache = false } = {}
  ) {
    const url = `/yard-task/${taskId}/timeline`;

    const axiosResponse = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },

  async updateYardTask(taskId, params, options = {}, requestConfig = {}) {
    const response = await axios.patch(`/yard-task/${taskId}`, params, requestConfig);
    return getResponse(response, options);
  }
};
